'use client';

import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type TermsPrivacyProps = {
  accepted: boolean;
  onChange: (accepted: boolean) => void;
};

const TERMS_TEXT = `Effective Date: October 25, 2025
Last Updated: October 25, 2025

IMPORTANT NOTICE
PLEASE READ THESE TERMS OF SERVICE CAREFULLY BEFORE USING THE SKILL4SKILL PLATFORM. BY ACCESSING OR USING OUR SERVICES, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, YOU MAY NOT ACCESS OR USE OUR SERVICES.

1. INTRODUCTION AND ACCEPTANCE OF TERMS
1.1 Agreement to Terms
These Terms of Service (the "Terms" or "Agreement") constitute a legally binding agreement between you (the "User," "you," or "your") and SKILL4SKILL INC. ("Skill4Skill," "Company," "we," "us," or "our"), a corporation incorporated under the Canada Business Corporations Act, with its registered office at 589 Knotridge Street, Ottawa, Ontario K1W 0C7.
1.2 Scope of Agreement
These Terms govern your access to and use of:
The Skill4Skill website located at "www.skill4skill.ca" (the "Website")
All subdomains, mobile-optimized versions, and mobile applications ("App") (if any)
All services, features, content, and functionality ("Services") offered through the Website or App (collectively, the "Platform")
1.3 Incorporation of Other Policies
The following documents are incorporated into and form an integral part of these Terms:
Privacy Policy: Governs the collection, use, and disclosure of your personal information
Community Guidelines: Sets forth standards of conduct and behavior expected of all Users
Cookie Policy: Explains our use of cookies and similar tracking technologies
In the event of any conflict between these Terms and the incorporated policies, these Terms shall prevail unless explicitly stated otherwise.
1.4 Acceptance and Modifications
By creating an account, accessing the Platform, uploading content, or using any of our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and all incorporated policies.
We reserve the right to modify, amend, or update these Terms at any time, with or without prior notice. Any changes will be effective immediately upon posting the updated Terms on the Website with a new "Last Updated" date. Your continued use of the Services after such changes constitutes your acceptance of the modified Terms. We encourage you to review these Terms periodically.
1.5 Language
These Terms have been prepared in English. In the event of any conflict between the English version and any translation, the English version shall prevail.

2. DESCRIPTION OF SERVICES
2.1 Platform Purpose
Skill4Skill is a community-driven online platform that facilitates peer-to-peer skill and service exchanges among Users. The Platform enables individuals to offer their skills, knowledge, or services to others in exchange for "SkillCoins," a virtual currency used exclusively within the Platform, thereby creating a cash-free barter marketplace.
2.2 Role as Intermediary
Skill4Skill operates strictly as a technology platform and intermediary that provides the venue, tools, and infrastructure to facilitate connections and exchanges between Users.
The Company:
Does NOT provide, perform, guarantee, supervise, or endorse any skills, services, or exchanges offered by Users
Does NOT employ, represent, or act as an agent for any User
Does NOT verify the qualifications, skills, credentials, experience, or background of Users
Is NOT a party to any agreement, transaction, or exchange between Users
Does NOT guarantee the quality, safety, legality, reliability, or completion of any User-provided service
Does NOT provide any warranty regarding User interactions or outcomes
2.3 Platform Features
The Platform provides the following features to facilitate skill exchanges:
User Profiles: Tools to create and maintain personal profiles showcasing skills, experience, and offerings
Search and Discovery: Functionality to search for and discover other Users based on skills, location, and other criteria
Messaging System: In-platform communication tools to negotiate terms, coordinate exchanges, and communicate with other Users
SkillCoin System: A virtual currency mechanism to facilitate and track skill exchanges
Review and Rating System: Tools to rate and review other Users after completing exchanges
Transaction History: Records of your skill exchanges and SkillCoin transactions
2.4 Service Availability
We strive to provide continuous, uninterrupted access to the Platform. However, we reserve the right to:
Modify, suspend, discontinue, or terminate the Services, in whole or in part, at any time
Perform scheduled or emergency maintenance
Implement updates, upgrades, or changes to features and functionality
Restrict access to certain features based on technical or business considerations
We do not guarantee that the Services will be available at all times or will be error-free, secure, or uninterrupted. You acknowledge that the Services are provided on an "as available" basis.
2.5 No Professional Services
The Platform is not intended to facilitate the provision of professional services requiring licensing, certification, or regulation under applicable law (such as legal, medical, financial, or therapeutic services). Users must ensure they comply with all applicable professional licensing and regulatory requirements when offering their skills.

3. USER ELIGIBILITY AND ACCOUNT REGISTRATION
3.1 Eligibility Requirements
To use the Services, you must:
(a) Age Requirement: Be at least 18 years of age or the age of majority in your province or territory of residence, whichever is greater
(b) Legal Capacity: Have the legal capacity to enter into a binding contract under applicable law
(c) Compliance: Not be prohibited from using the Services under any applicable law or prior agreement with Skill4Skill
(d) Good Standing: Not have been previously suspended or banned from the Platform for violating these Terms or our policies
3.2 Account Registration
(a) Required Information: To create an account, you must provide accurate, current, and complete information, including:
Full legal name
Valid email address
Location information (city/province)
Any other information reasonably requested during registration
(b) Account Security: You are solely responsible for:
Maintaining the confidentiality of your account credentials (username and password)
All activities that occur under your account
Ensuring that your password is strong, secure, and not shared with others
Immediately notifying us of any unauthorized use of your account or security breach
(c) Account Accuracy: You must:
Keep your account information current and accurate at all times
Promptly update any changes to your information through your account settings
Provide truthful and accurate information in your profile and skill listings
(d) Prohibited Actions: You must NOT:
Use another User's account without their express written permission
Allow any other person to use your account to access the Platform
Create multiple accounts for yourself without our prior written consent
Create an account using false, misleading, or inaccurate information
Impersonate any person or entity or misrepresent your affiliation with any person or entity
3.3 Account Types and Verification
(a) Standard Account: Available to all eligible Users who complete the registration process
(b) Verification: We reserve the right, but are not obligated, to implement verification procedures for Users, including identity verification, skill verification, or background checks
(c) Verified Badge: If we implement verification procedures, verified Users may receive a badge or designation on their profile. However, such verification does not constitute an endorsement, guarantee, or warranty of any kind regarding that User's skills, reliability, or trustworthiness
3.4 User Representations and Warranties
By creating an account, you represent and warrant that:
All information you provide is true, accurate, current, and complete
You will maintain and update such information to keep it true, accurate, current, and complete
You have the legal right and authority to enter into these Terms
Your use of the Services does not violate any applicable law or regulation
You will comply with all Terms and policies governing use of the Platform

4. USER CONDUCT AND ACCEPTABLE USE POLICY
4.1 General Standards of Conduct
All Users must treat each other and the Platform with respect, honesty, and integrity. You agree to:
(a) Honesty and Transparency: Be honest about your skills, experience, qualifications, and availability
(b) Professionalism: Conduct yourself in a professional, courteous, and respectful manner in all interactions
(c) Commitment: Honor commitments you make to other Users and complete services as agreed
(d) Quality: Deliver services with reasonable care, skill, and effort, and value others' time as you would your own
(e) Privacy: Respect the privacy and confidentiality of other Users and not misuse personal information shared with you
(f) Fair Use: Use SkillCoins fairly and in accordance with the Platform's intended purpose
4.2 Prohibited Activities
You expressly agree that you will NOT:
Illegal Activities:
Use the Services for any unlawful purpose or in violation of any applicable laws or regulations
Engage in criminal or tortious activity, including fraud, theft, harassment, stalking, or threatening behavior
Violate any intellectual property rights, including copyright, trademark, patent, or trade secret rights
Transmit, post, or share any content that is illegal, obscene, defamatory, libelous, threatening, harassing, hateful, racially or ethnically offensive, or otherwise objectionable
Harmful or Dangerous Activities:
Offer or request services that pose a risk of physical harm, danger, or injury
Provide professional services requiring licensing (legal, medical, financial advice) without proper credentials
Engage in any activity that could endanger the safety or well-being of other Users
Use the Platform to facilitate or solicit prostitution, escort services, or any adult or sexual services
Fraudulent or Deceptive Practices:
Provide false, misleading, inaccurate, or incomplete information about yourself or your skills
Misrepresent your qualifications, experience, credentials, or the nature of services you offer
Create fake or duplicate accounts
Engage in any form of fraud, scam, scheme, or deceptive practice
Manipulate or abuse the SkillCoin system, including creating fraudulent transactions or colluding with other Users to unfairly obtain coins
Abuse of Platform:
Use automated systems (bots, scripts, scrapers) to access or interact with the Platform without our express written permission
Attempt to gain unauthorized access to the Platform, other User accounts, or our systems or networks
Interfere with, disrupt, or create an undue burden on the Platform or its infrastructure
Reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code of the Platform
Remove, obscure, or alter any copyright, trademark, or proprietary rights notices
Spam and Commercial Misuse:
Send unsolicited messages, advertisements, promotional materials, spam, or chain letters to other Users
Use the Platform primarily for commercial advertising or self-promotion rather than genuine skill exchange
Harvest, collect, or use email addresses or other contact information of Users for unauthorized purposes
Use the Platform to redirect Users to external websites, services, or platforms for commercial purposes
Harassment and Abuse:
Harass, bully, intimidate, threaten, or abuse other Users
4.3 Content Standards
All content you post, upload, or share on the Platform (including profile information, skill descriptions, messages, reviews, and any other user-generated content) must:
Be accurate, truthful, and not misleading
Not violate any applicable law or regulation
Not infringe upon any intellectual property or proprietary rights of any third party
Not contain viruses, malware, or any harmful code
Comply with our Community Guidelines
4.4 Consequences of Violations
If you violate any provision of these Terms or our policies, we reserve the right, in our sole discretion and without prior notice or liability, to:
Issue a warning or request that you cease the violating conduct
Remove or disable access to any content you have posted
Suspend your access to some or all features of the Platform
Temporarily or permanently terminate your account
Reverse or withhold SkillCoin transactions
Report your conduct to law enforcement or regulatory authorities
Take legal action to enforce these Terms or protect our rights and interests
These remedies are in addition to any other rights or remedies we may have at law or in equity.
4.5 Reporting Violations
If you become aware of any User conduct that violates these Terms or our policies, or if you experience or witness harassment, fraud, or other concerning behavior, please report it immediately to us at support@skill4skill.ca. We will investigate all reports in accordance with our policies but are not obligated to take any specific action.

5. SKILL EXCHANGE TERMS
5.1 Nature of Skill Exchanges
User-to-User Contracts: When you agree to exchange skills with another User, you are entering into a direct, private agreement with that User. Skill4Skill is not a party to that agreement and has no responsibility or liability for the performance, non-performance, quality, safety, or legality of any User-provided service.
5.2 User Responsibilities in Exchanges
(a) Due Diligence: Before agreeing to an exchange, you are solely responsible for:
Reviewing the other User's profile, ratings, and reviews
Clearly communicating and agreeing upon the scope, terms, timing, and SkillCoin cost of the service
Verifying that the other User has the skills and qualifications they claim
Assessing any risks associated with the service
Taking appropriate safety precautions when meeting or interacting with other Users
(b) Clear Communication: You must clearly communicate with the other User to establish:
The specific service to be provided
The expected standard of performance or deliverables
The timeline for completion
The number of SkillCoins to be exchanged
Any materials, tools, or access required
The location where the service will be performed (if applicable)
Any other relevant terms or conditions
(c) Safety Precautions: When exchanging skills, especially when meeting in person, you must:
Meet in public, well-lit locations whenever possible
Inform a friend or family member of your plans
Trust your instincts and decline any exchange that makes you uncomfortable
Comply with all applicable health and safety regulations
Take appropriate measures to protect your personal property and safety
(d) Compliance with Law: You are solely responsible for ensuring that any skill exchange you participate in complies with all applicable federal, provincial, and municipal laws, regulations, licensing requirements, and industry standards.
5.3 Negotiation and Agreement Process
(a) Terms Negotiation: The Platform provides a messaging system for Users to negotiate and agree upon the terms of a skill exchange. All such negotiations and agreements are made directly between Users.
(b) Mutual Agreement Required: No User is obligated to accept any proposed exchange. Each User has the sole discretion to accept or decline any offer.
(c) Modification of Terms: Any modifications to an agreed-upon exchange must be mutually agreed upon by both parties through the Platform's messaging system.
5.4 Completion and Confirmation
(a) Service Completion: The User receiving the service (the "Recipient") is responsible for confirming when a service has been satisfactorily completed according to the agreed-upon terms.
(b) Confirmation Process: Upon completion, the Recipient must confirm task completion through the Platform, which triggers the transfer of the agreed-upon SkillCoins to the service provider.
(c) Finality: Once the Recipient confirms completion and SkillCoins are transferred, the transaction is considered final and complete, subject to Section 5.5.
5.5 Dispute Resolution
(a) Direct Resolution: If a dispute arises regarding the performance, quality, or terms of a skill exchange, Users must first attempt to resolve the matter directly through good-faith communication.
(b) Platform Assistance: Users may contact Skill4Skill for assistance in facilitating communication or providing guidance. However, we are not obligated to intervene in disputes between Users.
(c) Investigation: In cases of alleged fraud, non-performance, or serious violation of our Terms or Community Guidelines, we may, at our sole discretion, investigate the matter. Such investigation may include:
Reviewing messages exchanged between the parties
Reviewing the transaction history
Requesting additional information or evidence from the parties
Consulting with other Users who have interacted with the parties
(d) Platform Decisions: If we choose to intervene in a dispute, we may take actions including but not limited to:
Reversing or modifying a SkillCoin transaction
Issuing SkillCoins from our reserve to one or both parties as appropriate
Suspending or terminating one or both parties' accounts
Refusing to take any action
(e) Final and Binding: Subject to Sections 7.4 and 12.3, any decision Skill4Skill makes regarding a disputed SkillCoin transaction becomes final and binding once the dispute is marked as closed in the Platform and the Post Decision Escrow Period described in Section 7.4 has expired. After that point, the transaction cannot be reopened through the Platform and the SkillCoin allocation will not be reversed, except where required by law or where Skill4Skill chooses to correct a clear technical or accounting error.
(f) No Obligation: We have no obligation to monitor, mediate, or resolve disputes between Users. We may refuse to intervene in any dispute for any reason or for no reason.
5.6 Insurance and Liability
(a) No Insurance Provided: Skill4Skill does not provide any insurance coverage for injuries, damages, losses, or liabilities that may arise from skill exchanges between Users.
(b) User Responsibility: You are solely responsible for obtaining any insurance coverage you deem appropriate for the skills you offer or receive, including but not limited to general liability insurance, professional liability insurance, or property insurance.
(c) Verification: Before engaging in any exchange involving potential risk of property damage or personal injury, we strongly recommend verifying that the other User has appropriate insurance coverage.
5.7 Prohibited Services
The following types of services may NOT be offered or solicited on the Platform:
Services requiring professional licensing that you do not possess (legal, medical, financial advice, etc.)
Services that violate any applicable law, regulation, or industry standard
Adult services, escort services, dating services, or any sexually explicit services
Services involving weapons, firearms, explosives, or other dangerous materials
Services involving the sale or distribution of alcohol, tobacco, cannabis, or controlled substances
Services that pose an unreasonable risk of harm to persons or property
Any service that violates these Terms or our Community Guidelines

6. REVIEWS, RATINGS, AND FEEDBACK
6.1 Review System Purpose
The review and rating system allows Users to provide feedback about their experiences with other Users, thereby building trust and reputation within the community.
6.2 Posting Reviews
(a) Eligibility: You may only post reviews for Users with whom you have completed a skill exchange on the Platform.
(b) Honesty and Accuracy: All reviews must be based on your genuine, personal experience and must be honest, accurate, and fair.
(c) Content Standards: Reviews must:
Be constructive and respectful
Focus on the quality of the service and your experience
Not contain profanity, hate speech, discriminatory language, or personal attacks
Not disclose confidential or sensitive personal information about the other User
Comply with all provisions of Section 4 (User Conduct)
(d) Prohibited Review Content: You may not:
Post fake, false, or fraudulent reviews
Post reviews to extort, threaten, or unfairly damage another User's reputation
Offer or solicit compensation in exchange for positive reviews
Post reviews on behalf of other persons or entities
Use reviews primarily to promote your own services
6.3 Company Rights Regarding Reviews
(a) Moderation: We reserve the right, but are not obligated, to monitor, review, edit, or remove any review that:
Violates these Terms or our Community Guidelines
Is reported by another User as inappropriate
We determine, in our sole discretion, to be false, misleading, defamatory, or otherwise objectionable
(b) No Obligation: We have no obligation to monitor or verify the accuracy of reviews. We do not endorse or guarantee the accuracy, reliability, or truthfulness of any review.
(c) Responses: We may allow Users to respond to reviews posted about them. Any such responses are subject to the same standards and restrictions as original reviews.
6.4 Disputes Regarding Reviews
If you believe a review posted about you is false, defamatory, or violates these Terms, you may report it to us at support@skill4skill.ca. We will review your report in accordance with our policies but are not obligated to remove any review or take any specific action.
6.5 License to Reviews
By posting a review, you grant Skill4Skill an irrevocable, perpetual, worldwide, royalty-free, non-exclusive license to use, reproduce, display, and distribute your review in connection with the operation and promotion of the Platform, as set forth in Section 8 (Intellectual Property).

7. SKILLCOIN VIRTUAL CURRENCY SYSTEM
7.1 Nature of SkillCoins
(a) Definition: "SkillCoins" or "Coins" are a proprietary virtual currency that exists solely within the Skill4Skill Platform and is used exclusively to facilitate and account for skill exchanges between Users.
(b) Limited License: SkillCoins represent a limited, revocable, non-transferable license to use the Platform's exchange mechanism. They do not constitute:
Legal tender, currency, money, or a monetary equivalent
Property, assets, or any form of ownership interest in Skill4Skill
Securities, investments, or financial instruments of any kind
A store of value or medium of exchange outside the Platform
Credits redeemable for cash, goods, or services from Skill4Skill or any third party
(c) No Monetary Value: SkillCoins have no cash value, cannot be sold, exchanged for cash, redeemed for legal tender, or converted into any other form of value outside the Platform. The "1 SkillCoin ≈ $1 CAD value" reference used on the Platform is solely a notional guideline to help Users establish fair exchange rates and does not confer any actual monetary value. Coins are loyalty credits with no cash value and may be used only to account for completed skill exchanges between Users on the Platform within Canada. Coins are not gift cards, stored value, prepaid access, securities, money or monetary value. Coins cannot be redeemed for cash or any value from Skill4Skill or any third party.
7.2 Obtaining SkillCoins
SkillCoins may be obtained through the following methods, as determined by Skill4Skill:
(a) Initial Allocation: New Users may receive an initial allocation of SkillCoins upon account creation to facilitate their first exchanges
(b) Earning Through Service: Users earn SkillCoins by providing skills or services to other Users and receiving confirmation of satisfactory completion
(c) Promotional Grants: We may, at our discretion, grant SkillCoins as part of promotional campaigns, referral programs, or special events
7.3 Using SkillCoins
(a) Exchange for Services: SkillCoins may be used solely to "pay" for skills or services offered by other Users on the Platform, in accordance with mutually agreed-upon exchange rates.
(b) User-Set Pricing: Each User has the discretion to set their own SkillCoin rate for the services they offer, guided by the notional 1:1 equivalency with CAD for fairness and consistency.
(c) Transaction Process: SkillCoins are transferred from the Recipient's account to the service provider's account upon the Recipient's confirmation that the service has been satisfactorily completed.
(d) Balance Tracking: Your current SkillCoin balance is displayed in your account dashboard and reflects all completed transactions.
7.4 Transaction completion, escrow, and disputes
(a) Dual completion and escrow: For each SkillCoin transaction, the service provider must first mark the task as completed. This action places the agreed SkillCoins into escrow and prompts the recipient to either confirm completion or open a dispute. A transaction is considered completed only when both the service provider and the recipient mark the task as completed and no dispute has been opened. Once both parties confirm completion, the SkillCoins are released from escrow and transferred.
(b) Submission of disputes and review: If either party believes the service has not been completed as agreed, they must mark the task as disputed instead of marking it as completed. When a dispute is submitted, the SkillCoins remain in escrow and cannot be used until the dispute is resolved. Skill4Skill may review the information provided by both parties and determine how the SkillCoins will be allocated.
(c) Post Decision Escrow Period and finality: When Skill4Skill issues a decision on a disputed transaction, both parties will be notified and the SkillCoins will remain in escrow for three calendar days (the "Post Decision Escrow Period"). During this period, either party may submit additional evidence for review. Skill4Skill may, but is not required to, reconsider its decision based on any additional information. After the Post Decision Escrow Period expires, the dispute will be marked as closed, the SkillCoins will be released according to Skill4Skill's final decision, and the transaction will be considered completed.
7.5 Restrictions on SkillCoin Use
(a) No External Transfer: SkillCoins may not be transferred, sold, traded, or exchanged outside the Platform or with persons or entities not registered as Users.
(b) No Cash-Out: SkillCoins cannot be redeemed, converted, or exchanged for cash, legal tender, cryptocurrency, or any other form of value from Skill4Skill or any third party.
(c) Platform Use Only: SkillCoins have no utility, value, or use outside the Skill4Skill Platform.
(d) Prohibition on Manipulation: You may not engage in any conduct designed to manipulate, abuse, or exploit the SkillCoin system, including:
Creating fake transactions or exchanges to generate SkillCoins
Colluding with other Users to unfairly accumulate SkillCoins
Using multiple accounts to generate or transfer SkillCoins
Exploiting bugs, errors, or vulnerabilities in the SkillCoin system
7.6 Company Rights to Manage the SkillCoin Economy
We retain absolute and unilateral control over all aspects of the SkillCoin system and reserve the right, at any time and in our sole discretion, to:
(a) Modify the System: Change, modify, suspend, or discontinue the SkillCoin system or any aspect thereof
(b) Adjust Values: Adjust the notional value, purchasing power, or exchange rates associated with SkillCoins
(c) Manage Supply: Issue additional SkillCoins, remove SkillCoins from circulation, or adjust the total supply
(d) Correct Errors: Correct any errors in SkillCoin balances, transactions, or account records
(e) Adjust Individual Balances: Increase, decrease, or reset any User's SkillCoin balance for any reason, including as a penalty for violations of these Terms
(f) Implement Fees: Implement transaction fees, membership fees, or other charges related to SkillCoin use (with advance notice)
(g) Expiration Policy: Implement expiration dates or inactivity policies that result in the loss of unused SkillCoins
We will make reasonable efforts to provide advance notice of material changes to the SkillCoin system that adversely affect Users, but are not obligated to do so.
7.7 No Refunds or Compensation
(a) No Refunds: Under no circumstances will Skill4Skill provide refunds, compensation, or any form of monetary payment for SkillCoins, whether obtained for free or purchased (if purchasing is implemented).
(b) Termination: If your account is suspended or terminated, or if you voluntarily close your account, any SkillCoins in your account will be forfeited and you will have no right to compensation or recovery.
(c) Platform Changes: If we modify or discontinue the SkillCoin system, you will have no claim to compensation for any lost, devalued, or unusable SkillCoins.
7.8 Tax Implications
(a) User Responsibility: You are solely responsible for determining and satisfying any tax obligations that may arise from your use of SkillCoins or participation in skill exchanges on the Platform.
(b) Reporting: We may report certain transaction information to tax authorities as required by applicable law. You acknowledge and consent to such reporting.
(c) No Tax Advice: Nothing in these Terms constitutes tax advice. You should consult with a qualified tax professional regarding your specific situation.

8. INTELLECTUAL PROPERTY RIGHTS
8.1 Platform Intellectual Property
(a) Ownership: All intellectual property rights in and to the Platform, including but not limited to the Website, mobile applications, software, source code, object code, algorithms, designs, graphics, user interface, visual design, logos, trademarks, service marks, trade names ("Skill4Skill" and the Skill4Skill logo), documentation, and all content and materials created, published, or provided by Skill4Skill (collectively, the "Platform IP") are owned by or licensed to Skill4Skill.
(b) All Rights Reserved: All rights, title, and interest in the Platform IP are and shall remain the exclusive property of Skill4Skill and its licensors. These Terms do not grant you any ownership rights in the Platform IP.
(c) Limited License to Users: Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Platform for your personal, non-commercial use in accordance with these Terms.
(d) Restrictions: You may not:
Copy, modify, reproduce, distribute, display, perform, or create derivative works of the Platform IP
Reverse engineer, decompile, disassemble, or attempt to derive the source code of the Platform
Remove, obscure, or alter any copyright, trademark, or proprietary rights notices on the Platform
Use the Platform IP for any commercial purpose or in any way that competes with Skill4Skill
Frame or mirror any portion of the Platform on any other website
Use any automated tools (bots, scrapers) to access, copy, or monitor the Platform
8.2 User-Generated Content
(a) Definition: "User Content" means all content, information, data, materials, text, images, photographs, videos, audio, messages, reviews, skill descriptions, profile information, and any other materials that you submit, post, upload, or otherwise make available on or through the Platform.
(b) User Ownership: You retain all ownership rights in your User Content. However, by posting User Content on the Platform, you grant us certain rights as described below.
(c) License Grant to Skill4Skill: By posting User Content, you grant Skill4Skill and its successors a worldwide, royalty-free, non-exclusive, transferable and sublicensable license to host, reproduce, communicate, display, adapt and publish the User Content for the limited purpose of operating, improving and promoting the Platform and Services. This license continues for so long as the User Content is available on the Platform and for a reasonable period thereafter to allow for operational backup and archival copies.
(d) Moral Rights Waiver: To the extent permitted by applicable law, you consent to Skill4Skill performing the acts described in Section 8.2(c) in respect of your User Content and waive moral rights only to the extent strictly necessary to permit those acts.
(e) Deletion requests: If you delete User Content from your account, we will cease displaying it within a reasonable period. Copies may persist in backups for limited periods and where required by law or necessary to enforce these Terms.
8.3 User Content Representations and Warranties
By posting User Content, you represent and warrant that:
You own all rights in the User Content or have obtained all necessary permissions, licenses, consents, and releases
8.4 Content Removal and Monitoring
(a) Right to Remove: We reserve the right, but have no obligation, to monitor, review, edit, or remove any User Content at any time and for any reason, including if we determine that the content:
Violates these Terms or our Community Guidelines
Infringes upon intellectual property rights
Is illegal, harmful, offensive, or objectionable
Poses a risk to the Platform, other Users, or third parties
(b) No Obligation: We have no obligation to monitor User Content and do not pre-screen content before it is posted. The presence of User Content on the Platform does not indicate our endorsement, approval, or verification of that content.
8.5 Intellectual Property Infringement Claims
(a) Copyright Policy: We respect the intellectual property rights of others and expect our Users to do the same. If you believe that any User Content on the Platform infringes your copyright, please notify us at support@skill4skill.ca with the following information:
A description of the copyrighted work you claim has been infringed
The location (URL) where the allegedly infringing material appears on the Platform
Your contact information (name, address, email, telephone)
A statement that you have a good-faith belief that the disputed use is not authorized
A statement that the information in your notice is accurate and that you are the copyright owner or authorized to act on behalf of the copyright owner
Your physical or electronic signature
(b) Trademark Complaints: If you believe User Content infringes your trademark rights, please contact us using the same procedure.
(c) Counter-Notice: If your User Content is removed due to an intellectual property claim and you believe the removal was erroneous, you may submit a counter-notice to support@skill4skill.ca containing:
Identification of the removed material and its former location
A statement under penalty of perjury that you have a good-faith belief the material was removed by mistake or misidentification
Your contact information
Your consent to the jurisdiction of the Ontario courts
Your physical or electronic signature
(d) Repeat Infringers: We may, in appropriate circumstances, terminate the accounts of Users who repeatedly infringe intellectual property rights.
8.6 Feedback
If you provide us with any ideas, suggestions, feedback, or recommendations regarding the Platform ("Feedback"), you hereby grant us an unlimited, perpetual, irrevocable, worldwide, royalty-free, fully paid-up license to use, modify, and incorporate such Feedback into the Platform without any obligation to compensate you or provide attribution.

9. PRIVACY AND DATA PROTECTION
9.1 Privacy Policy
Your privacy is important to us. Our collection, use, disclosure, and protection of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Services, you consent to the data practices described in the Privacy Policy.
9.2 Key Privacy Principles
The Privacy Policy addresses:
What personal information we collect and how
How we use your information
When and with whom we share your information
Your rights regarding your personal information under Canadian privacy law
How we protect your information
How long we retain your information
9.3 Canadian Privacy Law Compliance
We are committed to complying with the Personal Information Protection and Electronic Documents Act (PIPEDA) and other applicable Canadian privacy laws. Our Privacy Policy provides detailed information on your rights under these laws.
9.4 User Obligations
You agree to:
Provide accurate personal information
Keep your information up to date
Protect the privacy of other Users
Not use personal information obtained through the Platform for purposes other than facilitating skill exchanges
Not share or misuse confidential information disclosed to you by other Users
9.5 Location Data
If you enable location-based features, you consent to our collection and use of your location data as described in the Privacy Policy. You may disable location services at any time through your device or account settings.
9.6 Communications
By creating an account, you consent to receive communications from us, including:
Transactional messages related to your account and exchanges
Platform updates and announcements
Marketing communications (which you may opt out of)
Responses to your inquiries
You may manage your communication preferences through your account settings or by following unsubscribe instructions in our emails. We will send marketing communications only with your express or implied consent under Canada's anti-spam law. You may withdraw consent at any time using the unsubscribe link in our emails or by contacting support@skill4skill.ca. Withdrawal does not affect transactional messages.

10. DISCLAIMERS AND LIMITATIONS OF LIABILITY
10.1 "AS IS" and "AS AVAILABLE" Disclaimer
YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT YOUR USE OF THE PLATFORM AND SERVICES IS AT YOUR SOLE RISK. THE PLATFORM AND SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITH ALL FAULTS, AND WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, SKILL4SKILL EXPRESSLY DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
(a) Warranties of merchantability, fitness for a particular purpose, title, and non-infringement
(b) Warranties regarding the accuracy, reliability, completeness, or timeliness of any content or information on the Platform
(c) Warranties that the Platform will be uninterrupted, secure, error-free, or free of viruses or other harmful components
(d) Warranties regarding the quality, safety, legality, or reliability of any User, service, or skill exchange facilitated through the Platform
10.2 No Guarantee of Results
We make no representations or warranties that:
You will find suitable Users to exchange skills with
Skill exchanges will meet your expectations
Disputes will be resolved to your satisfaction
The Platform will achieve any particular outcome for you
Your Skill Coin balance will retain any particular value or purchasing power
10.3 Third-Party Content and Services
The Platform may contain links to third-party websites, services, or resources. We are not responsible for and do not endorse or guarantee the availability, accuracy, or reliability of third-party content.
10.4 Disclaimer Regarding Users and User Services
SKILL4SKILL DOES NOT:
Vet, verify, screen, or conduct background checks on Users
Verify the skills, qualifications, credentials, experience, or representations of Users
Guarantee the identity, legitimacy, trustworthiness, or reliability of Users
Endorse or recommend any User or their services
Monitor or supervise skill exchanges or User interactions
Guarantee the quality, safety, legality, or completion of any User-provided service
YOU ACKNOWLEDGE AND AGREE THAT:
All interactions with other Users are at your own risk
You are solely responsible for your own due diligence, safety precautions, and assessment of other Users
We are not responsible for the conduct, actions, or omissions of any User
We have no obligation to intervene in disputes between Users
10.5 Limitation of Liability
TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SKILL4SKILL, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, LICENSORS, OR SERVICE PROVIDERS (COLLECTIVELY, THE "SKILL4SKILL PARTIES") BE LIABLE TO YOU OR ANY THIRD PARTY FOR:
ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, LOSS OF DATA, LOSS OF BUSINESS, LOSS OF GOODWILL, OR ANY OTHER INTANGIBLE LOSSES
ANY DAMAGES ARISING FROM:
Your use of or inability to use the Platform or Services
Any conduct or content of any User, including defamatory, offensive, or illegal conduct
Any User-provided service or skill exchange, including non-performance, poor performance, property damage, personal injury, or death
Unauthorized access to or alteration of your transmissions or data
Statements or conduct of any third party on the Platform
Any errors, bugs, or technical issues with the Platform
Loss or compromise of your SkillCoins or account
(c) ANY DAMAGES, WHETHER BASED ON CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR ANY OTHER LEGAL THEORY, EVEN IF THE SKILL4SKILL PARTIES HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES
10.6 Cap on Liability
TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE TOTAL AGGREGATE LIABILITY OF THE SKILL4SKILL PARTIES TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF THE PLATFORM, WHETHER IN CONTRACT, TORT, OR OTHERWISE, SHALL NOT EXCEED THE GREATER OF:
(a) THE AMOUNT YOU HAVE PAID TO SKILL4SKILL IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM; OR
(b) ONE HUNDRED CANADIAN DOLLARS (CAD $100.00)
10.7 Essential Basis of Bargain
You acknowledge and agree that the disclaimers and limitations of liability set forth in this Section 10 are fundamental elements of the bargain between you and Skill4Skill, and that the Platform and Services would not be provided to you without these limitations.
10.8 Jurisdictional Variations
Some jurisdictions do not allow the exclusion or limitation of certain warranties or liabilities. In such jurisdictions, the above disclaimers and limitations shall apply to the fullest extent permitted by applicable law, and our liability shall be limited to the maximum extent permitted by law.

11. INDEMNIFICATION
11.1 Your Obligation to Indemnify
You agree to indemnify, defend, and hold harmless the Skill4Skill Parties from and against any and all claims, demands, actions, suits, proceedings, investigations, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees and legal costs) arising out of or related to:
(a) Your use of or inability to use the Platform or Services
(b) Your violation of these Terms or any applicable law, regulation, or third-party right
(c) Your User Content or any content you post, upload, or make available on the Platform
(d) Your interactions with other Users, including any skill exchanges or services you provide or receive
(e) Any property damage, personal injury, death, or other harm arising from services you provide or receive
(f) Your violation of any intellectual property rights or other rights of any third party
(g) Any claim that your User Content caused damage to a third party
(h) Any fraudulent, negligent, or intentional act or omission by you
(i) Any claim by another User or third party arising from your conduct on or use of the Platform
11.2 Defence and Settlement
We reserve the right to assume the exclusive defence and control of any matter subject to indemnification by you, in which case you agree to cooperate with us in asserting any available defences. You may not settle any claim subject to this indemnification without our prior written consent.
11.3 Survival
Your indemnification obligations under this Section shall survive the termination of these Terms and your account.

12. DISPUTE RESOLUTION
12.1 Governing Law
These Terms and your use of the Platform shall be governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law principles.
12.2 Exclusive Jurisdiction for Disputes with Skill4Skill
(a) Agreement to Ontario Courts: You and Skill4Skill agree that any dispute, claim, or controversy arising out of or relating to these Terms or your use of the Platform (each, a "Dispute"), other than disputes between Users, shall be resolved exclusively in the courts located in Toronto, Ontario.
(b) Submission to Jurisdiction: You irrevocably submit to the exclusive jurisdiction of the Ontario courts and waive any objection to venue or inconvenient forum.
(c) Waiver of Jury Trial: To the extent permitted by law, you and Skill4Skill waive the right to a trial by jury for any Dispute.
12.3 User-to-User Disputes
Disputes between users regarding services, task completion, or SkillCoin transactions are primarily matters between the users. Skill4Skill is not a party to the underlying agreement between users. Users should attempt to resolve disagreements through direct communication. If we choose to investigate or take action regarding a User-to-User dispute, our decision will follow the procedure outlined in 5.5 and 7.4 herein.
12.4 Waiver of Class Actions
TO THE MAXIMUM EXTENT PERMITTED BY LAW, YOU AGREE THAT ANY DISPUTE SHALL BE BROUGHT IN AN INDIVIDUAL CAPACITY ONLY AND NOT AS PART OF ANY CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE ACTION OR PROCEEDING. You and Skill4Skill expressly waive the right to participate in class actions or class arbitrations. If you are a consumer with non-waivable statutory rights, nothing in this Section limits those rights.
12.5 Limitation on Time to Bring Claims
Any claim must be commenced within the shorter of the statutory period prescribed by applicable law and two years after the claim arose, except where a longer period is mandated by non-waivable law.

13. TERMINATION
13.1 Termination by You
You may terminate your account at any time by:
Accessing your account settings and following the account closure procedure
Sending a written request to support@skill4skill.ca
Upon termination, you will lose access to the Platform and all associated features.
13.2 Termination by Skill4Skill
We reserve the right to suspend, restrict, or terminate your account and access to the Platform at any time, with or without cause, with or without notice, and without liability to you, including if:
You violate any provision of these Terms or our policies
We are required to do so by law or legal process
Your conduct poses a risk to other Users, Skill4Skill, or third parties
Your account has been inactive for an extended period
We cease to offer the Services
We determine, in our sole discretion, that termination is appropriate
13.3 Effect of Termination
Upon termination of your account:
Access: Your right to access and use the Platform and all Services immediately ceases
SkillCoins: Any SkillCoins remaining in your account will be forfeited, and you will have no right to compensation, refund, or recovery
User Content: Your User Content may remain on the Platform to the extent it is incorporated in other Users' content (e.g., reviews of you) or as required by law. We may retain your data as described in our Privacy Policy.
License: The licenses you granted to us under Section 8.2 shall survive termination to the extent they are perpetual
Outstanding Obligations: You remain liable for any obligations incurred prior to termination
13.4 Survival
The following provisions shall survive termination of these Terms or your account: Sections 8 (Intellectual Property), 10 (Disclaimers and Limitations of Liability), 11 (Indemnification), 12 (Dispute Resolution), and any other provision that by its nature should survive.
13.5 No Liability for Termination
Skill4Skill shall not be liable to you or any third party for any termination, suspension, or restriction of your account or access to the Services.

14. GENERAL PROVISIONS
14.1 Entire Agreement
These Terms, together with the Privacy Policy and any other incorporated policies, constitute the entire agreement between you and Skill4Skill regarding the subject matter hereof and supersede all prior or contemporaneous communications, agreements, and understandings, whether written or oral.
14.2 Amendment of Terms
We reserve the right to modify, amend, or update these Terms at any time, in our sole discretion. We will notify you of material changes by:
Posting the updated Terms on the Website with a new "Last Updated" date
Sending notice to the email address associated with your account (if applicable)
Providing an in-app notification (if applicable)
Your continued use of the Platform after such changes constitutes your acceptance of the modified Terms. If you do not agree to the changes, you must stop using the Platform and terminate your account.
14.3 Waiver
No waiver of any provision of these Terms shall be deemed a further or continuing waiver of such provision or any other provision. Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
14.4 Severability
If any provision of these Terms is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such provision shall be modified to the minimum extent necessary to make it valid and enforceable, or if such modification is not possible, such provision shall be severed from these Terms. The remaining provisions shall continue in full force and effect.
14.5 Assignment
You may not assign, transfer, or delegate these Terms or any rights or obligations hereunder without our prior written consent. Any purported assignment in violation of this provision shall be null and void. We may freely assign these Terms and all rights and obligations to any third party without notice or your consent.
14.6 No Agency or Partnership
Nothing in these Terms creates any agency, partnership, joint venture, employer-employee, or franchisor-franchisee relationship between you and Skill4Skill. You have no authority to bind Skill4Skill or to act on behalf of Skill4Skill.
14.7 Force Majeure
Skill4Skill shall not be liable for any failure or delay in performing its obligations under these Terms due to circumstances beyond its reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, pandemics, labor disputes, governmental actions, telecommunications or internet failures, or third-party actions.
14.8 Notices
(a) To You: We may provide notices to you by:
Posting on the Platform
Sending an email to the address associated with your account
Sending an in-app notification
Sending physical mail to the address you provided
Notices by email or in-app notification are deemed received when sent. Notices by mail are deemed received five (5) business days after mailing.
(b) To Us: You may provide notice to Skill4Skill by sending an email to support@skill4skill.ca or by mailing to:
Skill4Skill Inc.
Attention: Legal Department
589 Knotridge Street
Ottawa, Ontario K1W 0C7
Canada
14.9 Headings
The section and paragraph headings in these Terms are for convenience only and shall not affect the interpretation of these Terms.
14.10 Electronic Signature and Acceptance
By clicking "I Agree," "Sign Up," or any similar button, or by accessing or using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms, and that such action constitutes your electronic signature and acceptance of these Terms.
14.11 Language
These Terms have been drafted in English. If these Terms are translated into any other language, the English version shall prevail in the event of any conflict or inconsistency.
14.12 Third-Party Beneficiaries
These Terms are for the benefit of Skill4Skill and you, and are not intended to confer any rights or benefits upon any third party.

15. CONTACT INFORMATION
If you have any questions, concerns, or complaints regarding these Terms or the Platform, please contact us at:
Skill4Skill Inc.
589 Knotridge Street
Ottawa, Ontario K1W 0C7
Canada
Email: support@skill4skill.ca

16. ACKNOWLEDGMENT
BY USING THE SKILL4SKILL PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU MAY NOT ACCESS OR USE THE PLATFORM.`;

const termsLines = TERMS_TEXT.split('\n');
const emailRegex = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;
const emailOnlyRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const renderTextWithEmailLinks = (text: string) =>
  text.split(emailRegex).map((part, idx) =>
    emailOnlyRegex.test(part) ? (
      <a
        key={`${part}-${idx}`}
        href={`mailto:${part}`}
        className='text-brand-green underline underline-offset-2 hover:text-brand-green/80'
      >
        {part}
      </a>
    ) : (
      part
    ),
  );

const renderTermsLine = (text: string, idx: number) => {
  const trimmedText = text.trim();
  if (!trimmedText) {
    return <div key={`sp-${idx}`} className='h-1' />;
  }

  if (trimmedText === 'TERMS OF SERVICE') {
    return (
      <h3
        key={`title-${idx}`}
        className='text-sm font-bold text-brand-dark sm:text-base'
      >
        {trimmedText}
      </h3>
    );
  }

  if (/^(Effective Date:|Last Updated:)/.test(trimmedText)) {
    return (
      <p
        key={`meta-${idx}`}
        className='text-[11px] font-medium text-gray-500 sm:text-xs'
      >
        {trimmedText}
      </p>
    );
  }

  if (trimmedText === 'IMPORTANT NOTICE') {
    return (
      <p
        key={`notice-${idx}`}
        className='py-2 text-[12px] font-semibold text-brand-dark-2 sm:text-[13px]'
      >
        {trimmedText}
      </p>
    );
  }

  if (/^[0-9]+\. /.test(trimmedText)) {
    return (
      <h4
        key={`section-${idx}`}
        className='pt-1 font-poppins text-[13px] font-semibold text-brand-dark sm:text-sm'
      >
        {trimmedText}
      </h4>
    );
  }

  if (/^[0-9]+\.[0-9]+ /.test(trimmedText)) {
    return (
      <h5
        key={`sub-${idx}`}
        className='font-poppins text-[12px] font-semibold text-brand-dark sm:text-[13px]'
      >
        {trimmedText}
      </h5>
    );
  }

  if (/^[A-Za-z][A-Za-z0-9\s'"/()-]+:$/.test(trimmedText)) {
    return (
      <p
        key={`label-${idx}`}
        className='font-poppins text-[12px] font-semibold text-gray-900 sm:text-[13px]'
      >
        {trimmedText}
      </p>
    );
  }

  if (/^\([a-z]\)/.test(trimmedText)) {
    return (
      <p
        key={`letter-${idx}`}
        className='pl-3 font-poppins text-[12px] text-gray-700 sm:text-[13px]'
      >
        {trimmedText}
      </p>
    );
  }

  if (
    /^[A-Z][^:]{2,}$/.test(trimmedText) &&
    trimmedText === trimmedText.toUpperCase()
  ) {
    return (
      <p key={`upper-${idx}`} className='font-semibold text-gray-900'>
        {trimmedText}
      </p>
    );
  }

  const isLikelyListItem =
    /^\([a-z]\)/.test(trimmedText) ||
    (!/[.!?]$/.test(trimmedText) &&
      !/:$/.test(trimmedText) &&
      !/^[0-9]+\. /.test(trimmedText) &&
      !/^[0-9]+\.[0-9]+ /.test(trimmedText));

  if (isLikelyListItem) {
    return (
      <p
        key={`li-${idx}`}
        className='flex gap-2 pl-1 text-[12px] text-gray-700 sm:text-[13px]'
      >
        <span className='pt-1 text-gray-500'>•</span>
        <span>{renderTextWithEmailLinks(trimmedText)}</span>
      </p>
    );
  }

  return (
    <p key={`line-${idx}`} className='text-[12px] text-gray-700 sm:text-[13px]'>
      {renderTextWithEmailLinks(trimmedText)}
    </p>
  );
};

type PrivacySection = {
  title: string;
  body: string[];
  items?: string[];
  footer?: string;
};

const privacySections: PrivacySection[] = [
  {
    title: '1. Privacy Policy',
    body: [
      'Your privacy is important to us. Our collection, use, disclosure, and protection of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.',
      'By using the Services, you consent to the data practices described in the Privacy Policy.',
    ],
  },
  {
    title: '2. Key Privacy Principles',
    body: ['The Privacy Policy addresses:'],
    items: [
      'What personal information we collect and how',
      'How we use your information',
      'When and with whom we share your information',
      'Your rights regarding your personal information under Canadian privacy law',
      'How we protect your information',
      'How long we retain your information',
    ],
  },
  {
    title: '3. Canadian Privacy Law Compliance',
    body: [
      'We are committed to complying with the Personal Information Protection and Electronic Documents Act (PIPEDA) and other applicable Canadian privacy laws.',
      'Our Privacy Policy provides detailed information on your rights under these laws.',
    ],
  },
  {
    title: '4. User Obligations',
    body: ['You agree to:'],
    items: [
      'Provide accurate personal information',
      'Keep your information up to date',
      'Protect the privacy of other Users',
      'Not use personal information obtained through the Platform for purposes other than facilitating skill exchanges',
      'Not share or misuse confidential information disclosed to you by other Users',
    ],
  },
  {
    title: '5. Location Data',
    body: [
      'If you enable location-based features, you consent to our collection and use of your location data as described in the Privacy Policy.',
      'You may disable location services at any time through your device or account settings.',
    ],
  },
  {
    title: '6. Communications',
    body: [
      'By creating an account, you consent to receive communications from us, including:',
    ],
    items: [
      'Transactional messages related to your account and exchanges',
      'Platform updates and announcements',
      'Marketing communications (which you may opt out of)',
      'Responses to your inquiries',
    ],
    footer:
      "You may manage your communication preferences through your account settings or by following unsubscribe instructions in our emails. We will send marketing communications only with your express or implied consent under Canada's anti-spam law. You may withdraw consent at any time using the unsubscribe link in our emails or by contacting support@skill4skill.ca. Withdrawal does not affect transactional messages.",
  },
];

export function TermsPrivacy({ accepted, onChange }: TermsPrivacyProps) {
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  return (
    <>
      <div className='flex items-start space-x-3'>
        <Checkbox
          id='acceptTerms'
          checked={accepted}
          onCheckedChange={(checked) => onChange(!!checked)}
          className='mt-px'
        />
        <label
          htmlFor='acceptTerms'
          className='text-sm leading-tight text-gray-600'
        >
          I agree to the{' '}
          <button
            type='button'
            onClick={() => setTermsModalOpen(true)}
            className='text-brand-green hover:underline'
          >
            Terms of Use
          </button>{' '}
          and{' '}
          <button
            type='button'
            onClick={() => setPrivacyModalOpen(true)}
            className='text-brand-green hover:underline'
          >
            Privacy Policy
          </button>
        </label>
      </div>

      <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
        <DialogContent className='max-h-[85vh] w-[95vw] overflow-y-auto break-words sm:max-w-[760px]'>
          <DialogHeader>
            <DialogTitle className='font-poppins text-lg font-semibold text-brand-base sm:text-xl'>
              Terms of Use
            </DialogTitle>
          </DialogHeader>

          <div className='text-[12px] leading-5 text-gray-700 sm:text-[13px]'>
            <div className='space-y-2'>{termsLines.map(renderTermsLine)}</div>
          </div>

          <div className='mt-6 flex justify-center'>
            <DialogClose asChild>
              <Button className='bg-brand-green hover:bg-brand-green/90'>
                Got it
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={privacyModalOpen} onOpenChange={setPrivacyModalOpen}>
        <DialogContent className='max-h-[85vh] w-[95vw] overflow-y-auto break-words sm:max-w-[760px]'>
          <DialogHeader>
            <DialogTitle className='font-poppins text-lg font-semibold text-brand-base sm:text-xl'>
              Privacy Policy
            </DialogTitle>
          </DialogHeader>

          <div className='mt-2 space-y-4 text-[12px] leading-5 text-gray-700 sm:text-[13px]'>
            <ol className='space-y-3'>
              {privacySections.map((section) => (
                <li key={section.title} className=''>
                  <p className='font-semibold text-brand-dark'>
                    {section.title}
                  </p>
                  <div className='mt-1 space-y-1.5 text-gray-700'>
                    {section.body.map((paragraph) => (
                      <p key={paragraph}>
                        {renderTextWithEmailLinks(paragraph)}
                      </p>
                    ))}
                    {section.items ? (
                      <ul className='space-y-1 pl-4'>
                        {section.items.map((item) => (
                          <li key={item} className='list-disc'>
                            {renderTextWithEmailLinks(item)}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {section.footer ? (
                      <p>{renderTextWithEmailLinks(section.footer)}</p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className='mt-6 flex justify-center'>
            <DialogClose asChild>
              <Button className='bg-brand-green hover:bg-brand-green/90'>
                Got it
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
